<template lang="pug">
.debugging-page.flex.column.gap-20(v-if="isAdmin")
  .desc 此頁面不在側欄顯示，僅供管理員除錯使用，可由網址 #/debugging 直接進入

  .section.flex.column.gap-10(v-if="impersonatedBy")
    .title.bold 檢視視角
    .desc 目前正以其他帳號的視角檢視，檢視視角相關功能暫時隱藏。點右上角「離開檢視視角」可回到 {{ impersonatedBy.name }}。

  .section.flex.column.gap-10(v-if="!isSuperAdmin && !impersonatedBy")
    .title.bold 檢視視角
    .desc 「管理平台帳號檢視器」與「專業人士檢視器」僅限超級管理員使用（需由工程人員以 set_super_admin 指令設定）。

  .section.flex.column.gap-10(v-if="isSuperAdmin && !impersonatedBy")
    .title.bold 管理平台帳號檢視器
    .desc 輸入 Email 搜尋後台帳號，點「開啟」會重新整理畫面並切換成該帳號的視角；右上角會改為「離開檢視視角」，點擊後切回原本帳號並回到此頁。
    .row.flex.align-center.gap-10.wrap
      el-input(
        v-model="accountKeyword"
        placeholder="輸入 Email 關鍵字"
        clearable
        style="width: 320px"
        @keyup.enter.native="searchAccounts")
      el-button(type="primary", size="small", :loading="accountLoading", @click="searchAccounts") 搜尋
    el-table(v-if="accountSearched", :data="matchedAccounts", border, size="small", style="width: 100%", empty-text="找不到符合的帳號")
      el-table-column(label="姓名", prop="name", width="140")
      el-table-column(label="Email", prop="email", min-width="220")
      el-table-column(label="角色", width="110")
        template(slot-scope="scope")
          span {{ roleLabel(scope.row.role) }}
      el-table-column(label="狀態", width="90")
        template(slot-scope="scope")
          span(v-if="scope.row.enabled") 啟用
          span.danger-color(v-else) 停用
      el-table-column(label="", width="100")
        template(slot-scope="scope")
          el-button(
            type="primary"
            size="mini"
            :disabled="!scope.row.enabled || scope.row.id === user.id"
            :loading="impersonatingID === scope.row.id"
            @click="impersonate(scope.row)") 開啟
    .desc.danger-color 注意：切換後的所有操作都會以該帳號的身份與權限執行，請只用於檢視。

  .section.flex.column.gap-10(v-if="isSuperAdmin && !impersonatedBy")
    .title.bold 專業人士檢視器
    .desc 選擇專業人士後點「前往」，會在新分頁以該專業人士的身份開啟專業人士平台，可檢視他實際看到的畫面。檢視連結為一次性、產生後 60 秒內有效。
    .row.flex.align-center.gap-10.wrap
      pro-selector(v-model="viewerProfessionalID", use-group, style="width: 320px")
      el-button(
        type="primary"
        size="small"
        :disabled="!viewerProfessionalID"
        :loading="viewerLoading"
        @click="goProfessionalSite") 前往
    .desc.danger-color 注意：開啟後在專業人士平台上的操作都會以該專業人士的身份執行，請只用於檢視。

  .section.flex.column.gap-10
    .title.bold 登入資訊
    el-table(:data="profileRows", border, size="small", :show-header="false", style="width: 100%")
      el-table-column(prop="key", width="200")
      el-table-column(prop="value")
    .row.flex.align-center.gap-10
      el-button(size="mini", @click="reloadProfile") 重新讀取 profile
      el-button(size="mini", @click="$copy(profileJSON, true)") 複製 profile JSON
    pre.code {{ profileJSON }}

  .section.flex.column.gap-10
    .title.bold 版本與環境
    el-table(:data="envRows", border, size="small", :show-header="false", style="width: 100%")
      el-table-column(prop="key", width="200")
      el-table-column(prop="value")

  .section.flex.column.gap-10
    .title.bold 時間戳轉換
    .desc 後端時間一律為 unix 秒；日期區間查詢的 end_at 要用當天 23:59:59
    .row.flex.align-center.gap-10.wrap
      el-input(
        v-model="timestampInput"
        placeholder="unix 秒"
        style="width: 200px"
        clearable)
      span.arrow →
      span {{ timestampToText }}
    .row.flex.align-center.gap-10.wrap
      el-date-picker(
        v-model="dateInput"
        type="datetime"
        placeholder="選擇日期時間"
        style="width: 220px")
      span.arrow →
      span {{ dateToTimestampText }}
      el-button(size="mini", v-if="dateToTimestampText", @click="$copy(dateToTimestampText, true)") 複製
    .row.flex.align-center.gap-10.wrap
      span 現在：{{ now }}
      span ({{ $timestampToDateTime(now) }}，{{ $getTimezoneString() }})

  .section.flex.column.gap-10
    .title.bold API 測試（GET）
    .desc 送出 GET 請求到 /api 底下的路徑並顯示原始回應，權限仍由後端判斷
    .row.flex.align-center.gap-10.wrap
      el-input(
        v-model="apiPath"
        placeholder="/v1/profile"
        style="width: 400px"
        @keyup.enter.native="sendRequest")
      el-button(type="primary", size="small", :loading="apiLoading", @click="sendRequest") 送出
      el-button(size="small", v-if="apiResponse", @click="$copy(apiResponse, true)") 複製回應
    .row.flex.align-center.gap-10(v-if="apiStatus !== undefined")
      span 狀態：
      span(:class="{'danger-color': apiStatus >= 400}") {{ apiStatus }}
      span(v-if="apiElapsed !== undefined") ，耗時 {{ apiElapsed }} ms
    pre.code(v-if="apiResponse") {{ apiResponse }}
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { getProfile } from '@/api/auth';
import instance from '@/api/base';
import { createImpersonationToken } from '@/api/professionals';
import { getAccounts, impersonateAccount } from '@/api/management';
import constant from '@/util/constant';

const ROLE_LABELS = {
  admin: '管理員',
  advstaff: '進階個管師',
  staff: '個管師',
  dispatcher: '派案人員',
  accounting: '帳務人員',
};

export default {
  name: 'setting-debugging',
  data() {
    return {
      accountKeyword: '',
      accounts: undefined,
      accountLoading: false,
      accountSearched: false,
      matchedAccounts: [],
      impersonatingID: undefined,
      viewerProfessionalID: undefined,
      viewerLoading: false,
      version: '-',
      now: 0,
      nowTimer: undefined,
      timestampInput: '',
      dateInput: undefined,
      apiPath: '/v1/profile',
      apiLoading: false,
      apiStatus: undefined,
      apiElapsed: undefined,
      apiResponse: '',
    };
  },
  computed: {
    ...mapGetters(['isAdmin', 'isSuperAdmin', 'impersonatedBy', 'user']),
    profileJSON() {
      return JSON.stringify(this.user || {}, null, 2);
    },
    profileRows() {
      const u = this.user || {};
      return [
        { key: 'ID', value: u.id ?? '-' },
        { key: '姓名', value: `${u.family_name || ''}${u.given_name || ''}` || '-' },
        { key: '角色', value: u.role || '-' },
        { key: '據點', value: (u.branches || []).map((b) => b.name).join('、') || '-' },
      ];
    },
    envRows() {
      return [
        { key: '版本', value: this.version },
        { key: '網址', value: window.location.origin },
        { key: '瀏覽器', value: window.navigator.userAgent },
        { key: '時區', value: this.$getTimezoneString() },
        { key: '螢幕', value: `${window.innerWidth} x ${window.innerHeight}` },
      ];
    },
    timestampToText() {
      const ts = parseInt(this.timestampInput, 10);
      if (Number.isNaN(ts)) {
        return '-';
      }
      return this.$timestampToDateTime(ts);
    },
    dateToTimestampText() {
      if (!this.dateInput) {
        return '';
      }
      return `${this.$dateToTimestamp(this.dateInput)}`;
    },
  },
  methods: {
    ...mapMutations(['setUserInfo']),
    roleLabel(role) {
      return ROLE_LABELS[role] || role || '-';
    },
    async searchAccounts() {
      const keyword = (this.accountKeyword || '').trim().toLowerCase();
      this.accountLoading = true;
      try {
        if (this.accounts === undefined) {
          this.accounts = await getAccounts();
        }
        this.matchedAccounts = this.accounts.filter((a) => (a.email || '').toLowerCase().includes(keyword));
        this.accountSearched = true;
      } catch (e) {
        this.$showAxiosException('讀取帳號列表失敗', e);
      } finally {
        this.accountLoading = false;
      }
    },
    async impersonate(account) {
      if (!account || !account.id) {
        return;
      }
      this.impersonatingID = account.id;
      try {
        await impersonateAccount(account.id);
        // 切換成該帳號後重新整理，讓整個管理端以該帳號的視角重新載入
        window.location.hash = '#/reserve';
        window.location.reload();
      } catch (e) {
        this.impersonatingID = undefined;
        this.$showAxiosException('切換檢視視角失敗', e);
      }
    },
    async goProfessionalSite() {
      const id = this.viewerProfessionalID;
      if (!id) {
        return;
      }
      // 先在使用者點擊的當下開新分頁，避免等 API 回來後才 window.open 被瀏覽器擋彈出視窗
      const win = window.open('', '_blank');
      this.viewerLoading = true;
      try {
        const rsp = await createImpersonationToken(id);
        const url = `${constant.proHost}/#/login?impersonation=${encodeURIComponent(rsp.token)}`;
        if (win) {
          win.location.href = url;
        } else {
          window.location.href = url;
        }
      } catch (e) {
        if (win) {
          win.close();
        }
        this.$showAxiosException('無法建立檢視連結', e);
      } finally {
        this.viewerLoading = false;
      }
    },
    async reloadProfile() {
      await this.$execWithLoading(async () => {
        const profile = await getProfile();
        this.setUserInfo(profile);
        this.$showSuccess('已重新讀取 profile');
      }, (e) => {
        this.$showAxiosException('讀取 profile 失敗', e);
      });
    },
    async sendRequest() {
      const path = (this.apiPath || '').trim();
      if (!path.startsWith('/')) {
        this.$showWarn('路徑需以 / 開頭');
        return;
      }
      this.apiLoading = true;
      this.apiStatus = undefined;
      this.apiElapsed = undefined;
      this.apiResponse = '';
      const startAt = Date.now();
      try {
        const rsp = await instance.get(path);
        this.apiStatus = rsp.status;
        this.apiResponse = JSON.stringify(rsp.data, null, 2);
      } catch (e) {
        if (e.response) {
          this.apiStatus = e.response.status;
          this.apiResponse = JSON.stringify(e.response.data, null, 2);
        } else {
          this.apiStatus = 0;
          this.apiResponse = `${e}`;
        }
      } finally {
        this.apiElapsed = Date.now() - startAt;
        this.apiLoading = false;
      }
    },
    tick() {
      this.now = Math.floor(Date.now() / 1000);
    },
  },
  created() {
    if (!this.isAdmin) {
      this.$router.replace('/reserve');
    }
  },
  async mounted() {
    if (!this.isAdmin) {
      return;
    }
    this.tick();
    this.nowTimer = setInterval(this.tick, 1000);
    this.version = await this.$getVersion();
  },
  beforeDestroy() {
    if (this.nowTimer) {
      clearInterval(this.nowTimer);
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

.debugging-page {
  overflow-y: auto;
  height: 100%;
}
.desc {
  color: $text-secondary-color;
  font-size: 12px;
}
.section {
  padding-bottom: 20px;
  border-bottom: 1px solid $border-light-color;
}
.arrow {
  color: $text-secondary-color;
}
.code {
  margin: 0;
  padding: 12px;
  max-height: 300px;
  overflow: auto;
  font-size: 12px;
  background: #F5F7FA;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
